import { createSelector, MemoizedSelector } from '@ngrx/store';
import {
  CmsSelectors,
  ContentSlotData,
  IndexType,
  Page,
  PageContext,
  PageState,
  PageType,
  StateUtils,
  StateWithCms,
} from '@spartacus/core';
import { CoreState, CustomIndexType, StateWithCore } from '../core-state';
import { getCustomCoreState } from './feature.selector';

const getIndexByType = (index: CustomIndexType & IndexType, type: string): StateUtils.EntityLoaderState<string> => {
  switch (type) {
    case PageType.CONTENT_PAGE: {
      return index.content;
    }
    case PageType.PRODUCT_PAGE: {
      return index.product;
    }
    case PageType.CATEGORY_PAGE: {
      return index.category;
    }
    case PageType.CATALOG_PAGE: {
      return index.catalog;
    }
    default:
      return index[type as keyof CustomIndexType] || { entities: {} };
  }
};

type StateWithCmsAndCore = StateWithCore & StateWithCms;

export const getPageStateIndex: MemoizedSelector<StateWithCmsAndCore, CustomIndexType & IndexType> = createSelector(
  CmsSelectors.getPageState as MemoizedSelector<StateWithCmsAndCore, PageState>,
  getCustomCoreState,
  (page: PageState, coreState: CoreState) => ({ ...page.index, ...coreState.customCms.pageIndex }),
);

export const getPageStateIndexEntityLoaderState = (
  pageContext: PageContext,
): MemoizedSelector<StateWithCms, StateUtils.EntityLoaderState<string>> =>
  createSelector(getPageStateIndex, (index: CustomIndexType & IndexType) => getIndexByType(index, pageContext.type));

export const getPageStateIndexLoaderState = (
  pageContext: PageContext,
): MemoizedSelector<StateWithCms, StateUtils.LoaderState<string>> =>
  createSelector(getPageStateIndexEntityLoaderState(pageContext), (indexState: StateUtils.EntityLoaderState<string>) =>
    StateUtils.entityLoaderStateSelector(indexState, pageContext.id),
  );

export const getPageStateIndexValue = (pageContext: PageContext): MemoizedSelector<StateWithCms, string> =>
  createSelector(getPageStateIndexLoaderState(pageContext), (entity: StateUtils.LoaderState<string>) =>
    StateUtils.loaderValueSelector<string>(entity),
  );

export const getPageData = (pageContext: PageContext): MemoizedSelector<StateWithCms, Page> =>
  createSelector(
    CmsSelectors.getPageEntities,
    getPageStateIndexValue(pageContext),
    (entities: { [id: string]: Page }, indexValue: string) => entities[indexValue],
  );

const getPageComponentTypesSelector: (page: Page) => string[] = (page: Page): string[] => {
  const componentTypes = new Set<string>();
  if (page && page.slots) {
    for (const slot of Object.keys(page.slots)) {
      for (const component of page.slots[slot].components || []) {
        componentTypes.add(component.flexType);
      }
    }
  }

  return Array.from(componentTypes);
};

export const getPageComponentTypes = (pageContext: PageContext): MemoizedSelector<StateWithCms, string[]> =>
  createSelector(getPageData(pageContext), (pageData) => getPageComponentTypesSelector(pageData));

export const getCurrentSlotSelectorFactory = (
  pageContext: PageContext,
  position: string,
): MemoizedSelector<StateWithCms, ContentSlotData> => {
  return createSelector(getPageData(pageContext), (entity) => {
    if (entity) {
      return entity.slots[position] || { components: [] };
    }

    return undefined;
  });
};
